<template>
  <div class="modal sign-modal" v-if="isVisible">
    <div class="overlay" @click="close()"></div>
    <div class="wrapper">
      <img class="close" @click="close" src="./../images/close.svg" />
      <div class="container">
        <div class="form">
          <div class="form-wrapper">
            <div class="form-fields">
              <div class="form-fields-wrapper">
                <div class="title">
                  {{ $t(`Please Confirm Your Transaction`) }}
                </div>

                <div class="email-confirmation">
                  <div class="email-icon">
                    <svg
                      width="64"
                      height="64"
                      viewBox="0 0 64 64"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M56 16H8C6.89543 16 6 16.8954 6 18V46C6 47.1046 6.89543 48 8 48H56C57.1046 48 58 47.1046 58 46V18C58 16.8954 57.1046 16 56 16Z"
                        stroke="#8c44ae"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M6 18L32 33L58 18"
                        stroke="#8c44ae"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>

                  <div class="message">
                    {{
                      $t(
                        `We've sent a confirmation email to your inbox. Please check your email and click the confirmation link to proceed with your transaction.`
                      )
                    }}
                  </div>

                  <div
                    class="timer-container"
                    :class="{ 'timer-warning': timeLeft <= 30 }"
                  >
                    <div class="timer-circle">
                      <svg width="120" height="120" viewBox="0 0 120 120">
                        <circle
                          class="timer-bg"
                          cx="60"
                          cy="60"
                          r="54"
                          fill="none"
                          stroke="#EAEEF4"
                          stroke-width="12"
                        ></circle>
                        <circle
                          class="timer-progress"
                          cx="60"
                          cy="60"
                          r="54"
                          fill="none"
                          stroke="#8c44ae"
                          stroke-width="12"
                          :stroke-dasharray="339.292"
                          :stroke-dashoffset="timerOffset"
                          transform="rotate(-90, 60, 60)"
                        ></circle>
                      </svg>
                      <div class="timer-time">{{ formattedTime }}</div>
                    </div>
                  </div>

                  <div v-if="status === 'expired'" class="status-message">
                    <div class="desc">
                      {{
                        $t(
                          `Confirmation time expired. Would you like to receive a new confirmation email?`
                        )
                      }}
                    </div>
                    <button
                      class="button"
                      @click="resendConfirmation"
                      :disabled="resending"
                    >
                      {{
                        resending ? $t(`Sending...`) : $t(`Resend Confirmation`)
                      }}
                    </button>
                  </div>

                  <div v-if="status === 'confirmed'" class="status-message">
                    <div class="desc">
                      {{
                        hasFiatPayment
                          ? $t(
                              `Transaction confirmed! Redirecting to payment...`
                            )
                          : $t(
                              `Transaction confirmed! Your order has been processed.`
                            )
                      }}
                    </div>
                  </div>

                  <div v-if="status === 'pending'" class="status-message">
                    <div class="desc">
                      {{ $t(`Didn't receive the email?`) }}
                    </div>
                    <div
                      class="link"
                      @click="resendConfirmation"
                      :class="{ disabled: resending }"
                    >
                      {{
                        resending ? $t(`Sending...`) : $t(`Resend Confirmation`)
                      }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "EmailConfirmationModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    orderUuid: {
      type: String,
      default: "",
    },
    expiresAt: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      timeLeft: 120,
      totalTime: 120,
      initialTimeLeft: 120,
      timerStartTime: null,
      recentlyUpdatedByServer: false,
      timer: null,
      checkStatusInterval: null,
      status: "pending",
      resending: false,
      internalVisible: false,
      internalOrderUuid: "",
      internalExpiresAt: 0,
      hasFiatPayment: false,
      apiErrorCount: 0,
    };
  },
  created() {
    if (window.showEmailConfirmation) {
      delete window.showEmailConfirmation;
    }
    window.showEmailConfirmation = this.showModal;
  },
  beforeDestroy() {
    this.stopTimer();
    this.stopStatusCheck();
    if (window.showEmailConfirmation === this.showModal) {
      delete window.showEmailConfirmation;
    }
  },
  computed: {
    isVisible() {
      return this.visible || this.internalVisible;
    },
    activeOrderUuid() {
      return this.orderUuid || this.internalOrderUuid;
    },
    activeExpiresAt() {
      return this.expiresAt || this.internalExpiresAt;
    },
    timerOffset() {
      const circumference = 339.292;
      const offset =
        circumference - (this.timeLeft / this.totalTime) * circumference;
      return offset;
    },
    formattedTime() {
      const minutes = Math.floor(this.timeLeft / 60);
      const seconds = this.timeLeft % 60;
      return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.startTimer();
        this.startStatusCheck();
      } else {
        this.stopTimer();
        this.stopStatusCheck();
        this.resetModal();
      }
    },
  },
  methods: {
    startTimer() {
      this.stopTimer();

      // First check if we have a valid expiration timestamp
      if (this.activeExpiresAt && this.activeExpiresAt > 0) {
        const now = Math.floor(Date.now() / 1000);
        const rawTimeLeft = this.activeExpiresAt - now;
        
        // If expiration is in the past or very close to now, force check the status first
        if (rawTimeLeft <= 5) {
          // Force an immediate status check before deciding on expiration
          this.$http.post(
            `transaction/check-status`,
            { order_uuid: this.activeOrderUuid }
          ).then(response => {
            if (response.data.expired) {
              this.status = "expired";
            } else if (response.data.remaining_seconds && response.data.remaining_seconds > 0) {
              // Server says there's still time left, trust the server
              this.timeLeft = response.data.remaining_seconds;
              this.initialTimeLeft = this.timeLeft;
              this.totalTime = this.timeLeft;
              this.timerStartTime = Date.now();
              this.startClientTimer();
            } else if (response.data.confirmed) {
              this.status = "confirmed";
            }
          }).catch(() => {
            // Fallback to client calculation
            this.timeLeft = Math.max(0, rawTimeLeft);
            this.initialTimeLeft = this.timeLeft;
            this.totalTime = this.timeLeft > 0 ? this.timeLeft : 120;
            
            if (this.timeLeft <= 0) {
              this.status = "expired";
            } else {
              this.startClientTimer();
            }
          });
          return; // Exit early as we'll handle timer start in the callbacks
        }
        
        // Normal case: expiration is reasonably in the future
        this.timeLeft = Math.max(0, rawTimeLeft);
        this.initialTimeLeft = this.timeLeft;
        this.totalTime = this.timeLeft > 0 ? this.timeLeft : 120;
      } else {
        // No expiration time provided, use default
        this.timeLeft = 120;
        this.totalTime = 120;
        this.initialTimeLeft = 120;
      }

      if (this.timeLeft <= 0) {
        this.status = "expired";
        return;
      }

      this.startClientTimer();
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    startStatusCheck() {
      this.stopStatusCheck();
      this.checkStatus();

      this.checkStatusInterval = setInterval(() => {
        this.checkStatus();
      }, 5000);
    },
    stopStatusCheck() {
      if (this.checkStatusInterval) {
        clearInterval(this.checkStatusInterval);
        this.checkStatusInterval = null;
      }
    },
    async checkStatus() {
      if (!this.activeOrderUuid) return;

      try {
        const response = await this.$http.post(
          `transaction/check-status`,
          { order_uuid: this.activeOrderUuid }
        );
        
        // Reset API error count on successful API call
        this.apiErrorCount = 0;

        // Check for expired status first, regardless of OK or ERROR status
        if (response.data.expired) {
          this.status = "expired";
          this.stopTimer();
          this.stopStatusCheck(); // Stop checking status after expiration
          return;
        }

        if (response.data.status === "OK") {
          if (response.data.confirmed) {
            this.status = "confirmed";
            this.stopTimer();
            this.stopStatusCheck();

            // Determine if this is a fiat-to-crypto transaction with payment URL
            this.hasFiatPayment = !!response.data.payment_url;

            setTimeout(() => {
              if (response.data.payment_url) {
                this.close();
                window.location.href = response.data.payment_url;
              } else {
                this.$emit("confirmed");
                this.close();
              }
            }, 2000);
          } else if (response.data.remaining_seconds || response.data.remaining_seconds === 0) {
            // Calculate expected client time based on elapsed time since last start/update
            const elapsedSeconds = Math.floor((Date.now() - this.timerStartTime) / 1000);
            const expectedTimeLeft = Math.max(0, this.initialTimeLeft - elapsedSeconds);
            
            // Determine difference between client calculation and server value
            const serverClientDiff = Math.abs(expectedTimeLeft - response.data.remaining_seconds);
            
            // If remaining_seconds is 0 but server doesn't say expired, check if client time is also very low
            if (response.data.remaining_seconds === 0 && expectedTimeLeft <= 5) {
              this.status = "expired";
              this.stopTimer();
              this.stopStatusCheck();
              return;
            }
            
            // Only update client timer if:
            // 1. Server time differs significantly (more than 3 seconds), OR
            // 2. Server has more time left than client (avoid premature expiration)
            if (serverClientDiff > 3 || (response.data.remaining_seconds > expectedTimeLeft && serverClientDiff > 1)) {
              // Update timer variables
              this.timeLeft = response.data.remaining_seconds;
              this.initialTimeLeft = response.data.remaining_seconds;
              this.timerStartTime = Date.now();
              this.recentlyUpdatedByServer = true;
            }
          }
        } else if (response.data.status === "ERROR" && response.data.message) {
          // Log error message but don't close modal unless expired (already handled above)
          console.error(`Status check error: ${response.data.message}`);
        }
      } catch (error) {
        console.error("Error checking confirmation status:", error);
        
        // Don't keep checking if there's a persistent API error
        if (this.apiErrorCount === undefined) {
          this.apiErrorCount = 1;
        } else {
          this.apiErrorCount++;
        }
        
        // After 3 consecutive errors, stop checking to prevent infinite errors
        if (this.apiErrorCount >= 3) {
          console.error("Too many API errors, stopping status checks");
          this.stopStatusCheck();
        }
      }
    },
    async resendConfirmation() {
      if (this.resending || !this.activeOrderUuid) return;

      this.resending = true;

      try {
        const response = await this.$http.post(
          `transaction/resend-confirmation`,
          { order_uuid: this.activeOrderUuid }
        );

        if (response.data.status === "OK") {
          if (response.data.expires_at) {
            this.internalExpiresAt = response.data.expires_at;
            this.status = "pending";
            
            // Stop existing intervals before starting new ones
            this.stopTimer();
            this.stopStatusCheck();
            
            // Start new timer and status check
            this.startTimer();
            this.startStatusCheck();
          }

          this.$toast(this.$t("Confirmation email has been resent"), {
            type: "success",
          });
        } else {
          this.$toast(this.$t("Failed to resend confirmation email"), {
            type: "error",
          });
        }
      } catch (error) {
        console.error("Error resending confirmation:", error);
        const errorMessage = error.response?.data?.message || "Failed to resend confirmation email";
        this.$toast(this.$t(errorMessage), {
          type: "error",
        });
      } finally {
        this.resending = false;
      }
    },
    close() {
      this.internalVisible = false;
      this.resetModal();
      this.$emit("close");
    },
    resetModal() {
      this.stopTimer();
      this.stopStatusCheck();
      this.status = "pending";
      this.internalOrderUuid = "";
      this.internalExpiresAt = 0;
      this.timeLeft = 120;
      this.initialTimeLeft = 120;
      this.totalTime = 120;
      this.timerStartTime = null;
      this.recentlyUpdatedByServer = false;
      this.apiErrorCount = 0;
    },
    showModal(orderUuid, expiresAt) {
      this.internalOrderUuid = orderUuid || this.orderUuid;
      this.internalExpiresAt = expiresAt || this.expiresAt;
      this.internalVisible = true;
      this.startTimer();
      this.startStatusCheck();
      return true;
    },
    // Separate timer start function to avoid code duplication
    startClientTimer() {
      // Store the start time for client-side timer calculation
      this.timerStartTime = Date.now();
      
      this.timer = setInterval(() => {
        // Calculate elapsed time since timer started
        const elapsedSeconds = Math.floor((Date.now() - this.timerStartTime) / 1000);
        
        // Calculate remaining time based on initial timeLeft minus elapsed time
        const calculatedTimeLeft = Math.max(0, this.initialTimeLeft - elapsedSeconds);
        
        // Only update if not recently updated by server
        if (!this.recentlyUpdatedByServer) {
          this.timeLeft = calculatedTimeLeft;
        }
        
        // Reset the server update flag after a cycle
        this.recentlyUpdatedByServer = false;
        
        if (this.timeLeft <= 0) {
          this.status = "expired";
          this.stopTimer();
        }
      }, 1000);
    },
  },
  mounted() {
    this.resetModal();
    if (this.visible) {
      this.startTimer();
      this.startStatusCheck();
    }
  },
};
</script>

<style scoped>
.email-confirmation {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
}

.email-icon {
  margin-bottom: 20px;
}

.message {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 24px;
  line-height: 1.5;
}

.timer-container {
  margin-bottom: 24px;
}

.timer-circle {
  position: relative;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.timer-bg {
  stroke: #eaeef4;
}

.timer-progress {
  transition: stroke-dashoffset 1s linear;
  stroke: #8c44ae;
}

.timer-time {
  position: absolute;
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
}

.timer-warning .timer-progress {
  stroke: #f59e0b;
}

.status-message {
  margin-top: 16px;
  width: 100%;
}

.desc {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 16px;
  line-height: 1.5;
}

.link {
  color: #8c44ae;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
  margin-top: 8px;
}

.link:hover {
  opacity: 0.8;
}

.link.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .timer-circle {
    width: 100px;
    height: 100px;
  }

  .timer-time {
    font-size: 20px;
  }

  .message {
    font-size: 13px;
  }
}
</style>
